// [목적] 생성 잡 진행 상태 폴링 훅 — GET /rag/conversations/:id/generation 을 주기 조회해 단계/완료/실패를 노출
// [이유] Spec(2026-07-11_openui-oneshot-pdf-folio) D-6(단계 progress) + Tech Review T9: 잡이 어차피
//        단계(추출→검색→생성→렌더)로 진행하므로 FE 가 폴링으로 진행률을 표시해야 한다. 기존 useRagConversation.ts
//        와 동일 스택(@tanstack/react-query, RTK Query 미사용)을 그대로 따른다.
// [사이드 이팩트] 없음 — 조회 전용 폴링(useQuery). status 가 done/failed 로 전이하면 refetchInterval 이
//                false 로 바뀌어 폴링이 자동 중단된다(추가 네트워크 요청 없음).
// [수정 시 고려사항] POLL_INTERVAL_MS(2초) 변경 시 BE 잡 처리 시간(수십 초 대기)과의 체감 균형을 재검토할 것.
//                   idle 상태(BE 가 아직 generationMeta 를 쓰지 않은 찰나)도 폴링을 계속해야 하므로
//                   중단 조건은 done|failed 두 값만 검사한다.

import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../api/http.js';
import { getGenerationStatus, type GenerationStatusResponse } from '../api/rag.client.js';
import { queryKeys } from '../api/queryKeys.js';

/** [목적] 폴링 간격(ms) 상수 — D-6 "수십 초 대기에 스피너만은 UX 부족" 요구를 2초 내외 갱신으로 충족 */
const POLL_INTERVAL_MS = 2000;

/** [목적] 폴링 중단 대상 상태 — 완료/실패 시 더 이상 조회할 필요가 없다 */
const TERMINAL_STATUSES = new Set<GenerationStatusResponse['status']>(['done', 'failed']);

/**
 * [목적] convId 기준 생성 잡 상태를 폴링하는 훅 — done/failed 도달 시 자동 중단
 * [이유] NewFolioPage 의 대기 화면이 이 훅 하나로 단계 progress·완료·실패 3가지 UI 분기를 모두 커버하게 한다.
 * [사이드 이팩트] getGenerationStatus → fetchWithEnvelope 경유(401 single-flight refresh 자동 처리 등 기존 규약 재사용).
 * [수정 시 고려사항] convId 가 없으면 쿼리 비활성(enabled=false) — NewFolioPage 는 phase==='waiting' 진입 시에만
 *                   convId 가 확정되므로 방어적으로 처리한다. 반환 형태는 BE 계약과 동일한 6필드 평면 구조
 *                   (portfolioId 포함, G1-1).
 */
export function useFolioGeneration(convId: string | undefined) {
  const query = useQuery<GenerationStatusResponse>({
    queryKey: convId ? queryKeys.rag.generation(convId) : ['rag', 'generation', 'noop'],
    queryFn: () => getGenerationStatus(convId!),
    enabled: !!convId,
    // [수정: QA-P1-01] 쿼리가 error 상태(예: 404 rag.SESSION_NOT_FOUND)로 확정된 뒤에도 기존 코드는
    // data 유무만 검사해 폴링을 멈추지 않았다 — 세션이 사라진 뒤 무한 폴링/무한 스피너로 이어졌다.
    // error 상태를 별도로 검사해 즉시 중단한다.
    refetchInterval: (q) => {
      if (q.state.status === 'error') return false;
      return q.state.data && TERMINAL_STATUSES.has(q.state.data.status) ? false : POLL_INTERVAL_MS;
    },
  });

  // [수정: QA-P1-01] 쿼리 error 를 status='failed' 로 매핑 — NewFolioPage 의 기존 failed 분기
  // (재시도 CTA, Stepper error 표시)를 그대로 재사용해 세션 만료를 사용자에게 노출한다.
  const queryErrorCode =
    query.error instanceof ApiError ? query.error.code : query.isError ? 'UNKNOWN_ERROR' : null;

  return {
    status: query.isError ? 'failed' : (query.data?.status ?? 'idle'),
    stage: query.data?.stage ?? null,
    pdfUrl: query.data?.pdfUrl ?? null,
    expiresAt: query.data?.expiresAt ?? null,
    errorCode: query.data?.errorCode ?? queryErrorCode,
    // [목적] 영속된 trip_portfolios.id — 존재하면 "폴리오 보기"(/folio/:id) 딥링크 CTA 노출용(G1-1)
    // [이유] BE 는 status 와 무관하게(render 실패로 failed 여도) 폴리오가 영속되어 있으면 값을 채운다
    //        (rag.client.ts GenerationStatusResponseSchema 주석 근거) — FE 도 상태 분기 없이 그대로 노출.
    portfolioId: query.data?.portfolioId ?? null,
    isLoading: query.isLoading,
  };
}
